import React, { useState, useMemo, useEffect } from "react";
import MetaTags from 'react-meta-tags';
import { useHistory, useParams } from "react-router-dom";
import { Container, Row, Col } from "reactstrap";
import { useTranslation } from "react-i18next";
import cookieHelper from "helpers/getCookieData";

// Icons from react-icons
import {
  RiSettings4Line,
  RiHospitalLine,
  RiUserHeartLine,
  RiGroupLine,
  RiShieldUserLine,
  RiStethoscopeLine,
  RiCapsuleLine,
  RiFileTextLine,
  RiMentalHealthLine,
  RiLayoutMasonryLine,
  RiShareForwardBoxLine,
  RiChat1Line,
  RiPriceTag3Line,
  RiGlobalLine,
  RiBankCardLine,
  RiNotification3Line,
  RiWhatsappLine,
  RiMailLine,
  RiQrCodeLine,
  RiServerLine,
  RiArrowLeftLine,
  RiArrowRightLine,
  RiSearchLine,
} from "react-icons/ri";

// User modules
import ManageClinic from "./user/ManageClinic";
import ManageDoctor from "./user/ManageDoctor";
import ManageUsers from "./user/ManageUsers";
import UserRoleAccess from "./user/UserRoleAccess";

// Master modules
import ManageTreatments from "./master/ManageTreatments";
import ManageMedicine from "./master/ManageMedicine";
import ManageDentalChart from "./master/ManageDentalChart";
import ManageRxTemplate from "./master/ManageRxTemplate";
import ManageSymstoms from "./master/ManageSymstoms";
import ManageChairs from "./master/ManageChairs";
import ManageReferences from "./master/ManageReferences";
import ManageCommunicationGroup from "./master/ManageCommunicationGroup";
import ManageTags from "./master/ManageTags";
import ManageLanguage from "./master/ManageLanguage";
import ManageBankAccount from "./master/ManageBankAccount";
import ManageInvoiceTemplate from "./master/ManageInvoiceTemplate";
import ManageReceiptTemplate from "./master/ManageReceiptTemplate";

// Communication modules
import Notifications from "./communication/Notifications";
import SMSTemplate from "./communication/SMSTemplate";
import EmailTemplate from "./communication/EmailTemplate";
import WhatsappConfig from "./communication/whatsappConfig";
import EmailConfig from "./communication/emailConfig";

import "./settings.scss";

// Direct component registry for 100% bug-free rendering
const MODULE_COMPONENTS = {
  ManageClinic,
  ManageDoctor,
  ManageUsers,
  UserRoleAccess,
  ManageTreatments,
  ManageMedicine,
  ManageDentalChart,
  ManageRxTemplate,
  ManageSymstoms,
  ManageChairs,
  ManageReferences,
  ManageCommunicationGroup,
  ManageTags,
  ManageLanguage,
  ManageBankAccount,
  ManageInvoiceTemplate,
  ManageReceiptTemplate,
  Notifications,
  SMSTemplate,
  EmailTemplate,
  WhatsappConfig,
  EmailConfig,
};

export const toKebabCase = (str) => {
  if (!str) return "";
  return str
    .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
    .replace(/[\s_]+/g, "-")
    .toLowerCase();
};

const findSettingsModule = (categoryModules, moduleParam) => {
  if (!moduleParam) return null;
  const lower = moduleParam.toLowerCase();
  const allList = Object.values(categoryModules).flat();
  return allList.find(
    (m) => m.id.toLowerCase() === lower || toKebabCase(m.id) === lower
  );
};

const Settings = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { category, module } = useParams();

  const clinic = useMemo(() => {
    try {
      const raw = cookieHelper.getCookie('_c');
      return raw ? JSON.parse(atob(raw)) : null;
    } catch (e) {
      return null;
    }
  }, []);

  const [activeCategory, setActiveCategory] = useState(category ? category.toLowerCase() : "user");
  const [selectedModule, setSelectedModule] = useState(module || null); // module key string
  const [searchTerm, setSearchTerm] = useState("");

  const categories = [
    {
      id: "user",
      title: "Practice & Team",
      icon: RiUserHeartLine,
      badge: "4 Modules",
    },
    {
      id: "master",
      title: "Clinical Masters",
      icon: RiStethoscopeLine,
      badge: "11 Modules",
    },
    {
      id: "communication",
      title: "Automations & Communication",
      icon: RiChat1Line,
      badge: "5 Modules",
    },
  ];

  const allModules = useMemo(() => ({
    user: [
      {
        id: "ManageClinic",
        title: "Clinic Profile & Branch",
        desc: "Manage clinic info, address, contact details, and operating timings",
        icon: RiHospitalLine,
        color: "#F8CA15",
        bg: "#e0f2fe",
        isActive: 1,
      },
      {
        id: "ManageDoctor",
        title: "Doctors & Specialists",
        desc: "Add and manage dental surgeons, specialization, and schedules",
        icon: RiUserHeartLine,
        color: "#059669",
        bg: "#d1fae5",
        isActive: 1,
      },
      {
        id: "ManageUsers",
        title: "Clinic Staff & Reception",
        desc: "Manage receptionist, assistant staff, and active logins",
        icon: RiGroupLine,
        color: "#d97706",
        bg: "#fef3c7",
        isActive: 1,
      },
      {
        id: "UserRoleAccess",
        title: "Roles & Permissions",
        desc: "Configure role-based access control and module permissions",
        icon: RiShieldUserLine,
        color: "#7c3aed",
        bg: "#ede9fe",
        isActive: 1,
      },
      {
        id: "SubscriptionBilling",
        title: "SaaS Subscription & Billing",
        desc: "Upgrade clinic plan, chair quotas, and PayU auto-renewal mandates",
        icon: RiBankCardLine,
        color: "#dc2626",
        bg: "#fee2e2",
        isActive: 1,
        directPath: "/subscription",
      },
    ],
    master: [
      {
        id: "ManageTreatments",
        title: "Treatments & Procedures",
        desc: "Catalog dental treatments, pricing, and duration master",
        icon: RiStethoscopeLine,
        color: "#F8CA15",
        bg: "#e0f2fe",
        isActive: 1,
      },
      {
        id: "ManageMedicine",
        title: "Medicine & Rx Drugs",
        desc: "Manage pharmacopeia, dosages, molecules, and frequencies",
        icon: RiCapsuleLine,
        color: "#2563eb",
        bg: "#dbeafe",
        isActive: 1,
      },
      {
        id: "ManageDentalChart",
        title: "Dental Charting",
        desc: "Adult & Pediatric tooth notation, conditions, and treatment maps",
        icon: RiLayoutMasonryLine,
        color: "#0d9488",
        bg: "#ccfbf1",
        isActive: 1,
      },
      {
        id: "ManageRxTemplate",
        title: "Prescription Templates",
        desc: "Pre-configured medicine sets for common dental diagnoses",
        icon: RiFileTextLine,
        color: "#7c3aed",
        bg: "#ede9fe",
        isActive: 1,
      },
      {
        id: "ManageSymstoms",
        title: "Clinical Symptoms",
        desc: "Configure standard symptoms and chief complaints",
        icon: RiMentalHealthLine,
        color: "#ea580c",
        bg: "#ffedd5",
        isActive: 1,
      },
      {
        id: "ManageChairs",
        title: "Dental Chairs",
        desc: "Manage operatories, chairs, and clinic chair assignments",
        icon: RiHospitalLine,
        color: "#059669",
        bg: "#d1fae5",
        isActive: 1,
      },
      {
        id: "ManageReferences",
        title: "Referral Sources",
        desc: "Track patient acquisition channels, referrers, and campaigns",
        icon: RiShareForwardBoxLine,
        color: "#4f46e5",
        bg: "#e0e7ff",
        isActive: 1,
      },
      {
        id: "ManageCommunicationGroup",
        title: "Communication Groups",
        desc: "Segment patients for targeted updates and announcements",
        icon: RiChat1Line,
        color: "#0891b2",
        bg: "#cffafe",
        isActive: 1,
      },
      {
        id: "ManageTags",
        title: "Patient Tags",
        desc: "Custom labels (VIP, Diabetics, High Priority, etc.)",
        icon: RiPriceTag3Line,
        color: "#db2777",
        bg: "#fce7f3",
        isActive: 1,
      },
      {
        id: "ManageLanguage",
        title: "Languages",
        desc: "Patient preferred languages for print and SMS templates",
        icon: RiGlobalLine,
        color: "#4b5563",
        bg: "#f3f4f6",
        isActive: 1,
      },
      {
        id: "ManageBankAccount",
        title: "Bank & Payment Accounts",
        desc: "Configure bank ledger, accounts, and settlement destinations",
        icon: RiBankCardLine,
        color: "#16a34a",
        bg: "#dcfce7",
        isActive: 1,
      },
      {
        id: "ManageInvoiceTemplate",
        title: "Invoice Templates",
        desc: "Customize clinic branding, layout colors, GSTIN, bank details, and notes for Tax Invoices",
        icon: RiFileTextLine,
        color: "#F8CA15",
        bg: "#e0f2fe",
        isActive: 1,
      },
      {
        id: "ManageReceiptTemplate",
        title: "Receipt Templates",
        desc: "Customize layout borders, thank-you notes, verified stamps, and terms for Money Receipts",
        icon: RiBankCardLine,
        color: "#059669",
        bg: "#d1fae5",
        isActive: 1,
      },
    ],
    communication: [
      {
        id: "Notifications",
        title: "Notification Rules",
        desc: "Automatic reminders for appointments, birthdays, and recalls",
        icon: RiNotification3Line,
        color: "#F8CA15",
        bg: "#e0f2fe",
        isActive: 1,
      },
      {
        id: "SMSTemplate",
        title: "WhatsApp Templates",
        desc: "Manage automated WhatsApp message templates and triggers",
        icon: RiWhatsappLine,
        color: "#16a34a",
        bg: "#dcfce7",
        isActive: 1,
      },
      {
        id: "EmailTemplate",
        title: "Email Templates",
        desc: "Manage HTML and rich text email confirmation templates",
        icon: RiMailLine,
        color: "#2563eb",
        bg: "#dbeafe",
        isActive: 1,
      },
      {
        id: "WhatsappConfig",
        title: "WhatsApp QR Gateway",
        desc: "Scan QR code to connect clinic WhatsApp Web gateway",
        icon: RiQrCodeLine,
        color: "#059669",
        bg: "#d1fae5",
        isActive: 1,
      },
      {
        id: "EmailConfig",
        title: "SMTP Email Server",
        desc: "Configure custom SMTP host, SSL/TLS ports, and credentials",
        icon: RiServerLine,
        color: "#7c3aed",
        bg: "#ede9fe",
        isActive: 1,
      },
    ],
  }), [clinic]);

  useEffect(() => {
    const cat = category ? category.toLowerCase() : "user";
    if (allModules[cat]) {
      setActiveCategory(cat);
    }
    if (module) {
      const match = findSettingsModule(allModules, module);
      if (match) {
        setSelectedModule(match.id);
      }
    } else {
      setSelectedModule(null);
    }
  }, [category, module, allModules]);

  // Handle category selection
  const handleCategorySelect = (categoryId) => {
    const catSlug = categoryId.toLowerCase();
    setActiveCategory(catSlug);
    setSelectedModule(null);
    setSearchTerm("");
    history.push(`/settings/${catSlug}`);
  };

  // Handle module selection
  const handleOpenModule = (moduleId, categoryId) => {
    if (moduleId === "SubscriptionBilling") {
      history.push("/subscription");
      return;
    }
    const targetCat = (categoryId || activeCategory).toLowerCase();
    setActiveCategory(targetCat);
    setSelectedModule(moduleId);
    history.push(`/settings/${targetCat}/${toKebabCase(moduleId)}`);
  };

  const handleBackToHub = () => {
    setSelectedModule(null);
    history.push(`/settings/${activeCategory.toLowerCase()}`);
  };

  // Filter modules based on search
  const filteredModules = useMemo(() => {
    if (!searchTerm.trim()) {
      return allModules[activeCategory] || [];
    }
    const term = searchTerm.toLowerCase();
    const list = [];
    Object.keys(allModules).forEach(catKey => {
      allModules[catKey].forEach(mod => {
        if (
          mod.title.toLowerCase().includes(term) ||
          mod.desc.toLowerCase().includes(term) ||
          mod.id.toLowerCase().includes(term)
        ) {
          list.push({ ...mod, categoryId: catKey });
        }
      });
    });
    return list;
  }, [allModules, activeCategory, searchTerm]);

  // Current active subcategory module component
  const ActiveComponent = selectedModule ? MODULE_COMPONENTS[selectedModule] : null;
  const currentCategoryObj = categories.find(c => c.id === activeCategory);
  const currentModuleObj = Object.values(allModules)
    .flat()
    .find(m => m.id === selectedModule);

  return (
    <React.Fragment>
      <div className="page-content settings-page-wrapper">
        <MetaTags>
          <title>{t("Settings")} | Oralstop OPD</title>
        </MetaTags>

        <Container fluid>
          {/* Top Hero Header */}
          <div className="settings-hero-header">
            <div className="settings-hero-title-group">
              <div className="settings-hero-icon">
                <RiSettings4Line />
              </div>
              <div>
                <h1 className="settings-hero-title">{t("Clinical & System Settings")}</h1>
                <p className="settings-hero-subtitle">
                  {t("Configure treatments, medicines, operatory chairs, staff access, and WhatsApp integrations")}
                </p>
              </div>
            </div>

            <div className="settings-search-box">
              <RiSearchLine />
              <input
                type="text"
                className="form-control settings-search-input"
                placeholder={`${t("Search")}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* If a module is selected, render workspace with sidebar */}
          {selectedModule ? (
            <div className="settings-workspace-wrapper">
              <div className="settings-breadcrumb-bar">
                <button
                  type="button"
                  className="btn-back-to-settings"
                  onClick={handleBackToHub}
                >
                  <RiArrowLeftLine />
                  <span>{t("All Settings")}</span>
                </button>

                <div className="d-flex align-items-center gap-2 font-size-13 text-muted">
                  <span>{t(currentCategoryObj?.title) || currentCategoryObj?.title}</span>
                  <span>/</span>
                  <strong className="text-slate-800">{t(currentModuleObj?.title) || currentModuleObj?.title}</strong>
                </div>
              </div>

              <Row className="g-4">
                {/* Left Navigation Sidebar */}
                <Col lg={3} md={4}>
                  <div className="settings-sidebar-nav-card sticky-top" style={{ top: '80px', zIndex: 10 }}>
                    <div className="settings-sidebar-header">
                      {t(currentCategoryObj?.title) || currentCategoryObj?.title}
                    </div>
                    <ul className="settings-sidebar-list">
                      {(allModules[activeCategory] || []).map((mod) => {
                        const Icon = mod.icon;
                        const isCurrent = mod.id === selectedModule;
                        return (
                          <li key={mod.id}>
                            <button
                              type="button"
                              className={`settings-sidebar-item-btn ${isCurrent ? 'is-selected' : ''}`}
                              onClick={() => handleOpenModule(mod.id, activeCategory)}
                            >
                              <Icon style={{ color: mod.color }} />
                              <span className="text-truncate">{t(mod.title) || mod.title}</span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </Col>

                {/* Right Workspace Content Panel */}
                <Col lg={9} md={8}>
                  {ActiveComponent ? (
                    <ActiveComponent />
                  ) : (
                    <div className="card border-0 shadow-sm text-center py-5 text-muted">
                      <p>Module component not found.</p>
                      <div>
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          onClick={handleBackToHub}
                        >
                          {t("Settings")}
                        </button>
                      </div>
                    </div>
                  )}
                </Col>
              </Row>
            </div>
          ) : (
            /* Settings Hub Grid View */
            <div>
              {/* Category Tabs (Only when not searching) */}
              {!searchTerm.trim() && (
                <div className="settings-category-tabs">
                  {categories.map((cat) => {
                    const CatIcon = cat.icon;
                    const isActive = activeCategory === cat.id;
                    return (
                      <button
                        key={cat.id}
                        type="button"
                        className={`settings-category-tab-btn ${isActive ? 'is-active' : ''}`}
                        onClick={() => handleCategorySelect(cat.id)}
                      >
                        <CatIcon />
                        <span>{t(cat.title) || cat.title}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Module Cards Grid */}
              <div className="settings-module-grid">
                {filteredModules.map((mod) => {
                  const Icon = mod.icon;
                  const targetCat = mod.categoryId || activeCategory;
                  return (
                    <div
                      key={mod.id}
                      role="button"
                      tabIndex={0}
                      className="settings-module-card"
                      onClick={() => handleOpenModule(mod.id, targetCat)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleOpenModule(mod.id, targetCat); }}
                    >
                      <div>
                        <div className="settings-module-card-top">
                          <div
                            className="settings-module-icon-circle"
                            style={{ background: mod.bg, color: mod.color }}
                          >
                            <Icon />
                          </div>
                          <h4 className="settings-module-title">{t(mod.title) || mod.title}</h4>
                        </div>
                        <p className="settings-module-desc">{t(mod.desc) || mod.desc}</p>
                      </div>

                      <div className="settings-module-footer">
                        <span className="settings-open-link">
                          <span>{t("View Details")}</span>
                          <RiArrowRightLine />
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default Settings;
